import React, { useState, useEffect, useReducer, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  PhoneMissed as PhoneMissedIcon,
  PhoneCallback as PhoneCallbackIcon,
  PhoneForwarded as PhoneForwardedIcon,
  Refresh as RefreshIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusChip: {
    minWidth: 80,
  },
  directionIcon: {
    marginRight: theme.spacing(1),
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "flex-end",
  },
  searchField: {
    flex: 1,
  },
  filterField: {
    minWidth: 120,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CALLS") {
    const calls = action.payload;
    const newCalls = [];

    calls.forEach((call) => {
      const callIndex = state.findIndex((c) => c.id === call.id);
      if (callIndex !== -1) {
        state[callIndex] = call;
      } else {
        newCalls.push(call);
      }
    });

    return [...state, ...newCalls];
  }

  if (action.type === "UPDATE_CALL") {
    const call = action.payload;
    const callIndex = state.findIndex((c) => c.id === call.id);

    if (callIndex !== -1) {
      state[callIndex] = call;
      return [...state];
    } else {
      return [call, ...state];
    }
  }

  if (action.type === "DELETE_CALL") {
    const callId = action.payload;
    const callIndex = state.findIndex((c) => c.id === callId);
    if (callIndex !== -1) {
      state.splice(callIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Calls = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDirection, setSelectedDirection] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [calls, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, selectedStatus, selectedDirection]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCalls();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, selectedStatus, selectedDirection, pageNumber]);

  useEffect(() => {
    const companyId = user?.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-call`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CALL", payload: data.call });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CALL", payload: +data.callId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user]);

  const fetchCalls = async () => {
    try {
      const { data } = await api.get("/calls", {
        params: {
          searchParam,
          pageNumber,
          status: selectedStatus,
          direction: selectedDirection,
        },
      });
      dispatch({ type: "LOAD_CALLS", payload: data.calls });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toast.error("Error al cargar las llamadas");
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "answered":
        return "primary";
      case "missed":
        return "secondary";
      case "rejected":
        return "default";
      case "busy":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "answered":
        return "Contestada";
      case "missed":
        return "Perdida";
      case "rejected":
        return "Rechazada";
      case "busy":
        return "Ocupado";
      default:
        return status;
    }
  };

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case "incoming":
        return <PhoneCallbackIcon className={classes.directionIcon} />;
      case "outgoing":
        return <PhoneForwardedIcon className={classes.directionIcon} />;
      default:
        return <PhoneIcon className={classes.directionIcon} />;
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return "-";
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Llamadas</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Actualizar">
            <IconButton onClick={() => window.location.reload()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar llamadas..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "grey" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl className={classes.filterField}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="answered">Contestadas</MenuItem>
              <MenuItem value="missed">Perdidas</MenuItem>
              <MenuItem value="rejected">Rechazadas</MenuItem>
              <MenuItem value="busy">Ocupado</MenuItem>
            </Select>
          </FormControl>
          <FormControl className={classes.filterField}>
            <InputLabel>Dirección</InputLabel>
            <Select
              value={selectedDirection}
              onChange={(e) => setSelectedDirection(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="incoming">Entrantes</MenuItem>
              <MenuItem value="outgoing">Salientes</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Dirección</TableCell>
              <TableCell align="center">De</TableCell>
              <TableCell align="center">Para</TableCell>
              <TableCell align="center">Contacto</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Duración</TableCell>
              <TableCell align="center">Fecha/Hora</TableCell>
              <TableCell align="center">Conexión</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.map((call) => (
              <TableRow key={call.id}>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center">
                    {getDirectionIcon(call.direction)}
                    {call.direction === "incoming" ? "Entrante" : "Saliente"}
                  </Box>
                </TableCell>
                <TableCell align="center">{call.from}</TableCell>
                <TableCell align="center">{call.to}</TableCell>
                <TableCell align="center">
                  {call.contact ? call.contact.name : "-"}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={getStatusLabel(call.status)}
                    color={getStatusColor(call.status)}
                    size="small"
                    className={classes.statusChip}
                  />
                </TableCell>
                <TableCell align="center">
                  {formatDuration(call.duration)}
                </TableCell>
                <TableCell align="center">
                  {format(new Date(call.startTime), "dd/MM/yyyy HH:mm", {
                    locale: es,
                  })}
                </TableCell>
                <TableCell align="center">
                  {call.whatsapp ? call.whatsapp.name : "-"}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton avatar columns={8} />}
          </TableBody>
        </Table>
        {hasMore && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="outlined"
              onClick={loadMore}
              disabled={loading}
            >
              Cargar más
            </Button>
          </Box>
        )}
        {calls.length === 0 && !loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography variant="h6" color="textSecondary">
              No se encontraron llamadas
            </Typography>
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Calls;